import React from 'react';
import { useTranslation } from 'react-i18next';
import { BsLightningCharge } from 'react-icons/bs';
import { FaBaby } from 'react-icons/fa6';
import { FiChevronRight, FiLoader, FiX } from 'react-icons/fi';
import cn from 'classnames';

import { Button } from '../common/Button';

export type StartExamChoice = 'realtime' | 'ob';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (choice: StartExamChoice) => void;
  isBusy?: boolean;
};

export default function StartExamChoiceModal({
  isOpen,
  onClose,
  onSelect,
  isBusy = false,
}: Props) {
  const { t } = useTranslation('Scheduling');

  if (!isOpen) return null;

  const options: Array<{
    id: StartExamChoice;
    title: string;
    description: string;
    icon: React.ReactNode;
    iconClass: string;
  }> = [
    {
      id: 'realtime',
      title: t('startExamChoice.realtime', { defaultValue: 'Realtime exam' }),
      description: t('startExamChoice.realtimeDescription', {
        defaultValue: 'Live scanning with detected view cards and report.',
      }),
      icon: <BsLightningCharge className="h-4 w-4" />,
      iconClass:
        'bg-blue-600 text-white dark:bg-blue-500',
    },
    {
      id: 'ob',
      title: t('startExamChoice.ob', { defaultValue: 'OB exam' }),
      description: t('startExamChoice.obDescription', {
        defaultValue: 'Create a prenatal OB exam and open the report viewer.',
      }),
      icon: <FaBaby className="h-4 w-4" />,
      iconClass:
        'bg-emerald-600 text-white dark:bg-emerald-500',
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4"
      onClick={isBusy ? undefined : onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-exam-choice-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 bg-slate-50/80 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="min-w-0">
            <h3
              id="start-exam-choice-title"
              className="text-base font-bold text-slate-900 dark:text-white"
            >
              {t('startExamChoice.title', {
                defaultValue: 'Choose exam type',
              })}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {t('startExamChoice.hint', {
                defaultValue:
                  'Start a realtime exam or create an obstetric (OB) exam for this appointment.',
              })}
            </p>
          </div>
          <button
            type="button"
            disabled={isBusy}
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-white hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-white"
            aria-label={t('cancel')}
          >
            <FiX className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-2 p-4">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              disabled={isBusy}
              onClick={() => onSelect(option.id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-left transition',
                'hover:border-blue-300 hover:bg-blue-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                'disabled:cursor-not-allowed disabled:opacity-60',
                'dark:border-gray-600 dark:bg-gray-800 dark:hover:border-blue-700 dark:hover:bg-blue-900/20'
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg shadow-sm',
                  option.iconClass
                )}
                aria-hidden
              >
                {option.icon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-900 dark:text-white">
                  {option.title}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-slate-500 dark:text-slate-400">
                  {option.description}
                </span>
              </span>
              <FiChevronRight
                className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-500"
                aria-hidden
              />
            </button>
          ))}

          {isBusy ? (
            <div className="flex items-center justify-center gap-2 pt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
              <FiLoader className="h-3.5 w-3.5 animate-spin" />
              {t('startExamChoice.starting', {
                defaultValue: 'Starting exam…',
              })}
            </div>
          ) : null}
        </div>

        <footer className="flex justify-end border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          <Button
            variant="secondary"
            size="sm"
            disabled={isBusy}
            onClick={onClose}
          >
            {t('cancel')}
          </Button>
        </footer>
      </div>
    </div>
  );
}
